import type { HelpCenterTemplateKey } from '@/pages/HelpCenter/types'

interface TemplateThumbnailProps {
  template: HelpCenterTemplateKey
  primary: string
  accent: string
}

// Low-fi, themeable SVG previews of each home template. Deliberately abstract
// (blocks, not real text) so they read at ~160px and re-tint to the brand.
// Adding a template = add a case here + an entry in TEMPLATE_OPTIONS.
export function TemplateThumbnail({ template, primary, accent }: TemplateThumbnailProps) {
  const bg = 'var(--hc-thumb-bg, #f4f5f7)'
  const card = '#ffffff'
  const line = '#d7dbe0'
  const common = { rx: 2 }

  return (
    <svg viewBox="0 0 160 108" className="h-auto w-full" role="img" aria-label={`${template} layout preview`}>
      <rect x="0" y="0" width="160" height="108" fill={bg} />
      {/* header bar (shared) */}
      <rect x="0" y="0" width="160" height="12" fill={card} />
      <rect x="8" y="4" width="20" height="4" fill={primary} {...common} />
      <rect x="120" y="4" width="14" height="4" fill={line} {...common} />
      <rect x="138" y="4" width="14" height="4" fill={primary} {...common} />

      {template === 'classic' && (
        <>
          <rect x="30" y="22" width="100" height="7" fill={line} rx="2" />
          <rect x="40" y="33" width="80" height="8" fill={card} stroke={line} rx="2" />
          <rect x="46" y="36" width="6" height="2" fill={primary} rx="1" />
          {[8, 58, 108].map(x => (
            <rect key={x} x={x} y="52" width="44" height="24" fill={card} stroke={line} rx="2" />
          ))}
          <rect x="8" y="84" width="144" height="6" fill={card} stroke={line} rx="2" />
          <rect x="8" y="94" width="144" height="6" fill={card} stroke={line} rx="2" />
        </>
      )}

      {template === 'aurora' && (
        <>
          {/* mesh hero */}
          <defs>
            <linearGradient id="auroraHero" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor={primary} stopOpacity="0.9" />
              <stop offset="1" stopColor={accent} stopOpacity="0.85" />
            </linearGradient>
          </defs>
          <rect x="0" y="12" width="160" height="40" fill="url(#auroraHero)" />
          <rect x="46" y="24" width="68" height="7" fill="#ffffff" opacity="0.95" rx="2" />
          <rect x="40" y="37" width="80" height="9" fill="#ffffff" rx="3" />
          {/* asymmetric bento */}
          <rect x="8" y="58" width="86" height="42" fill={card} stroke={line} rx="2" />
          <rect x="14" y="64" width="8" height="8" fill={primary} rx="2" />
          <rect x="100" y="58" width="52" height="20" fill={card} stroke={line} rx="2" />
          <rect x="104" y="62" width="6" height="6" fill={accent} rx="1" />
          <rect x="100" y="80" width="52" height="20" fill={card} stroke={line} rx="2" />
          <rect x="104" y="84" width="6" height="6" fill={primary} rx="1" />
        </>
      )}

      {template === 'atlas' && (
        <>
          {/* sidebar */}
          <rect x="0" y="12" width="44" height="96" fill={card} />
          <line x1="44" y1="12" x2="44" y2="108" stroke={line} />
          {[20, 30, 40, 54, 64, 74, 88].map((y, i) => (
            <rect key={y} x="8" y={y} width={i % 3 === 0 ? 28 : 22} height="4"
              fill={i === 2 ? primary : line} rx="1" />
          ))}
          <rect x="6" y="52" width="3" height="4" fill={accent} />
          {/* main */}
          <rect x="54" y="20" width="70" height="7" fill={line} rx="2" />
          <rect x="54" y="34" width="46" height="6" fill={card} stroke={line} rx="2" />
          {[46, 104].map(x => (
            <rect key={x} x={x} y="48" width="48" height="22" fill={card} stroke={line} rx="2" />
          ))}
          {[46, 104].map(x => (
            <rect key={`b${x}`} x={x} y="76" width="48" height="22" fill={card} stroke={line} rx="2" />
          ))}
        </>
      )}
    </svg>
  )
}
